import React from "react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuCheckboxItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "../ui/dropdown-menu";
import { MagnifyingGlass, FunnelX, Columns, X } from "@phosphor-icons/react";
import {
  MODULES, PRIORITIES, OVERALL_STATUSES, RISK_LEVELS, DEV_STATUSES, TASK_COLUMNS,
} from "../../lib/constants";

const ALL = "__all__";

const FILTERS = [
  { key: "module", label: "Module", options: MODULES },
  { key: "priority", label: "Priority", options: PRIORITIES },
  { key: "overall_status", label: "Overall Status", options: OVERALL_STATUSES },
  { key: "risk_level", label: "Risk", options: RISK_LEVELS },
  { key: "development_status", label: "Dev Status", options: DEV_STATUSES },
];

export default function FilterBar({
  filters,
  onChange,
  onClear,
  visibleColumns,
  setVisibleColumns,
}) {
  const activeCount = Object.values(filters).filter((v) => v && v !== "").length;

  return (
    <div className="flex flex-col md:flex-row items-stretch md:items-center gap-3">
      <div className="relative flex-1 min-w-[220px]">
        <MagnifyingGlass
          size={16}
          weight="bold"
          className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400"
        />
        <Input
          data-testid="filter-search-input"
          value={filters.search || ""}
          onChange={(e) => onChange({ ...filters, search: e.target.value })}
          placeholder="Search by ID, title, comments..."
          className="pl-9 h-10 bg-white"
        />
        {filters.search ? (
          <button
            type="button"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-700"
            onClick={() => onChange({ ...filters, search: "" })}
            data-testid="filter-search-clear"
          >
            <X size={14} weight="bold" />
          </button>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => (
          <Select
            key={f.key}
            value={filters[f.key] || ALL}
            onValueChange={(v) => onChange({ ...filters, [f.key]: v === ALL ? "" : v })}
          >
            <SelectTrigger
              data-testid={`filter-${f.key}-dropdown`}
              className="h-10 w-[150px] bg-white"
            >
              <SelectValue placeholder={f.label} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All {f.label}</SelectItem>
              {f.options.map((o) => (
                <SelectItem key={o} value={o}>
                  {o}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ))}

        <Button
          variant="outline"
          size="sm"
          onClick={onClear}
          disabled={activeCount === 0}
          data-testid="filter-clear-btn"
          className="h-10"
        >
          <FunnelX size={16} weight="bold" className="mr-2" />
          Clear
          {activeCount > 0 ? (
            <span className="ml-2 text-xs bg-neutral-900 text-white rounded-full h-5 w-5 flex items-center justify-center">
              {activeCount}
            </span>
          ) : null}
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" data-testid="column-visibility-btn" className="h-10">
              <Columns size={16} weight="bold" className="mr-2" />
              Columns
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="max-h-96 overflow-y-auto w-56" align="end">
            <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {TASK_COLUMNS.map((c) => (
              <DropdownMenuCheckboxItem
                key={c.key}
                data-testid={`column-toggle-${c.key}`}
                checked={visibleColumns.includes(c.key)}
                onCheckedChange={(checked) => {
                  if (checked) setVisibleColumns([...visibleColumns, c.key]);
                  else setVisibleColumns(visibleColumns.filter((k) => k !== c.key));
                }}
              >
                {c.label}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
